import { PAGE_ROUTES } from '@/components/nav/utils/url-builders';
import FullscreenMessage from '@/components/ui/layout/fullscreen-message';
import { LOCAL_STORAGE_KEYS } from '@/lib/local-storage-keys';
// DECOMMISSION: Core hydration is stripped to only what the archived-deals page
// and the Inbox need. Archived deals are fetched on-demand and arrive with their
// associations embedded; they only require hubs, users, lenders, and vehicle
// listings (for VIN/vehicle stitching) to be in memory. Notifications power the
// Inbox. Everything else (active deals, tasks, leads, appraisals, etc.) is gone.
import {
  hydrateHubs,
  hydrateLenders,
  hydrateNotifications,
  hydrateUsers,
  hydrateVehiclesAndListingsFromIndexer,
} from '@/models/hydrate';
import { useQuery } from '@tanstack/react-query';

import { observer } from 'mobx-react-lite';
import { createContext, useContext, useMemo, useState } from 'react';
import useSideBarHydrateLatest from './optimizations/layout/sidebar/use-sidebar-hydrate-latest';
import useSideBarHydrateLocal from './optimizations/layout/sidebar/use-sidebar-hydrate-local';
import useContractQueuePageHydrateLatest from './optimizations/page/contract-queue/use-contract-queue-page-hydrate-latest';
import useContractQueuePageHydrateLocal from './optimizations/page/contract-queue/use-contract-queue-page-hydrate-local';
import useDealPageHydrateLocal from './optimizations/page/deal/use-deal-page-hydrate-local';
import useDealsPageHydrateLatest from './optimizations/page/deals/use-deals-page-hydrate-latest';
import useDealsPageHydrateLocal from './optimizations/page/deals/use-deals-page-hydrate-local';
import useDeliveryQueuePageHydrateLatest from './optimizations/page/delivery-queue/use-delivery-queue-page-hydrate-latest';
import useDeliveryQueuePageHydrateLocal from './optimizations/page/delivery-queue/use-delivery-queue-page-hydrate-local';
import useInboxPageHydrateLocal from './optimizations/page/inbox/use-inbox-page-hydrate-local';
import useLeadsPageHydrateLocal from './optimizations/page/leads/use-leads-page-hydrate-local';
import useMyDealsPageHydrateLatest from './optimizations/page/my-deals/use-my-deals-page-hydrate-latest';
import useMyDealsPageHydrateLocal from './optimizations/page/my-deals/use-my-deals-page-hydrate-local';
import useMyTasksPageHydrateLocal from './optimizations/page/my-tasks/use-my-tasks-page-hydrate-local';
import useTasksPageHydrateLocal from './optimizations/page/tasks/use-tasks-page-hydrate-local';

type HydrationContextType = {
  isHydrated: boolean;
  isAPIHydrating: boolean;
  requiresFullHydrate: boolean;
  setRequiresFullHydrateToTrue: () => void;
};

const HydrationContext = createContext<HydrationContextType | undefined>(
  undefined,
);

const PAGES_WITH_OPTIMIZED_HYDRATION = [
  PAGE_ROUTES.INBOX,
  PAGE_ROUTES.DEALS,
  PAGE_ROUTES.MY_DEALS,
  PAGE_ROUTES.DEAL,
  PAGE_ROUTES.TASKS,
  PAGE_ROUTES.MY_TASKS,
  PAGE_ROUTES.LEADS,
  PAGE_ROUTES.CONTRACT_QUEUE,
  PAGE_ROUTES.DELIVERY_QUEUE,
];
const PAGES_THAT_DO_NOT_REQUIRE_OPTIMIZED_HYDRATION = [
  PAGE_ROUTES.ARCHIVED_DEALS,
  PAGE_ROUTES.AUTH,
  PAGE_ROUTES.PROFILE,
  PAGE_ROUTES.CONNECTED_ACCOUNTS,
  PAGE_ROUTES.ABOUT,
  PAGE_ROUTES.HOTKEYS,
];
const PAGES_THAT_CANNOT_DO_FULL_API_HYDRATION = [
  // DEV NOTE: these pages cannot do full API hydration because it'll lock the scroll
  // because of the multiple virtualized lists
  PAGE_ROUTES.DEALS,
  PAGE_ROUTES.MY_DEALS,
  PAGE_ROUTES.CONTRACT_QUEUE,
  // DEV NOTE: the delivery queue page can do full API hydration
  // even though it has virtualized lists because the lists are so small
];

type HydrationTask = () => Promise<unknown>;

const BACKGROUND_HYDRATION_CONCURRENCY = 4;
const shouldLogHydrationTiming = import.meta.env.DEV;

const startHydrationTimer = (label: string) => {
  if (shouldLogHydrationTiming) {
    console.time(label);
  }
};

const endHydrationTimer = (label: string) => {
  if (shouldLogHydrationTiming) {
    console.timeEnd(label);
  }
};

const runHydrationTasksWithConcurrency = async (
  tasks: HydrationTask[],
  concurrency = BACKGROUND_HYDRATION_CONCURRENCY,
): Promise<void> => {
  let nextTaskIndex = 0;
  const workerCount = Math.min(concurrency, tasks.length);

  const workers = Array.from({ length: workerCount }, async () => {
    while (nextTaskIndex < tasks.length) {
      const taskIndex = nextTaskIndex;
      nextTaskIndex += 1;
      const task = tasks[taskIndex];
      if (!task) {
        return;
      }
      await task();
    }
  });

  await Promise.all(workers);
};

export const HydrationProvider: React.FC<{
  children: React.ReactNode;
}> = observer(({ children }) => {
  const {
    isLoading,
    isAPIHydrating,
    isDoingFullHydrate,
    requiresFullHydrate,
    setRequiresFullHydrateToTrue,
  } = useHydrate();

  const pageRequiresOptimizationLoadingMessage = () => {
    return ![
      ...PAGES_WITH_OPTIMIZED_HYDRATION,
      ...PAGES_THAT_DO_NOT_REQUIRE_OPTIMIZED_HYDRATION,
    ].some((page) => window.location.pathname.includes(`/${page}`));
  };

  if (
    isDoingFullHydrate &&
    isAPIHydrating &&
    pageRequiresOptimizationLoadingMessage()
  ) {
    return (
      <div>
        <FullscreenMessage>
          <div className="text-2xl">Setting up your workspace...</div>
        </FullscreenMessage>
      </div>
    );
  }

  if (
    !isDoingFullHydrate &&
    isAPIHydrating &&
    pageRequiresOptimizationLoadingMessage()
  ) {
    return (
      <div>
        <FullscreenMessage>
          <div className="text-2xl">Setting up your workspace...</div>
          <p>This page does not have optimized loading...</p>
        </FullscreenMessage>
      </div>
    );
  }

  return (
    <HydrationContext.Provider
      value={{
        isHydrated: !isLoading,
        isAPIHydrating,
        requiresFullHydrate,
        setRequiresFullHydrateToTrue,
      }}
    >
      {children}
    </HydrationContext.Provider>
  );
});

export const useHydrationContext = () => {
  const context = useContext(HydrationContext);
  if (!context) {
    throw new Error(
      'useHydrationContext must be used within a HydrationProvider',
    );
  }
  return context;
};

const useHydrateSidebarAndPage = () => {
  // UX Note: we always show the sidebar first to get a good initial loading UX
  // PERFORMANCE NOTE: For the pages, we generally do the local hydration first
  // so users see the data as soon as possible, and then we do the latest hydration
  // to get eventual consistency with the DB
  useSideBarHydrateLocal();

  useDealPageHydrateLocal();

  useDealsPageHydrateLocal();
  useDealsPageHydrateLatest();

  useMyDealsPageHydrateLocal();
  useMyDealsPageHydrateLatest();

  useContractQueuePageHydrateLocal();
  useContractQueuePageHydrateLatest();

  useDeliveryQueuePageHydrateLocal();
  useDeliveryQueuePageHydrateLatest();

  useTasksPageHydrateLocal();

  useMyTasksPageHydrateLocal();

  useLeadsPageHydrateLocal();

  useInboxPageHydrateLocal();

  useSideBarHydrateLatest();
};

const useHydrateApp = () => {
  const [requiresFullHydrate, setRequiresFullHydrate] = useState(false);
  const setRequiresFullHydrateToTrue = () => {
    if (!requiresFullHydrate) {
      setRequiresFullHydrate(true);
    }
  };
  const isDexieEmpty =
    localStorage.getItem(LOCAL_STORAGE_KEYS.DEXIE_SYNC_CHECK_LAST_UPDATED) ===
    null;

  const calcOnPageThatCanDoFullApiHydration = () => {
    return !PAGES_THAT_CANNOT_DO_FULL_API_HYDRATION.some((page) =>
      window.location.pathname.includes(`/${page}`),
    );
  };
  const onPageThatCanDoFullApiHydration = useMemo(
    () => calcOnPageThatCanDoFullApiHydration(),
    [calcOnPageThatCanDoFullApiHydration],
  );

  const { isLoading: isAPIHydrating } = useQuery({
    queryKey: ['hydrate'],
    queryFn: async () => {
      startHydrationTimer('hydrate:app');

      // IMPORTANT: Do NOT wrap this in runInAction(async () => {...}).
      // runInAction doesn't await async callbacks — it returns after the first
      // `await`, leaving the rest fire-and-forget. This causes queryFn to resolve
      // before stores are populated, so the UI renders with stale/missing data.
      // DECOMMISSION: stripped to archived-deals + Inbox dependencies only.
      const criticalHydrationTasks: HydrationTask[] = [
        hydrateHubs,
        hydrateUsers,
        hydrateVehiclesAndListingsFromIndexer,
        hydrateNotifications,
      ];

      const backgroundHydrationTasks: HydrationTask[] = [hydrateLenders];

      try {
        startHydrationTimer('hydrate:app:critical');
        await Promise.all(criticalHydrationTasks.map((task) => task()));
        endHydrationTimer('hydrate:app:critical');

        startHydrationTimer('hydrate:app:background');
        await runHydrationTasksWithConcurrency(backgroundHydrationTasks);
        endHydrationTimer('hydrate:app:background');
      } finally {
        endHydrationTimer('hydrate:app');
      }

      localStorage.setItem(
        LOCAL_STORAGE_KEYS.DEXIE_SYNC_CHECK_LAST_UPDATED,
        new Date().toISOString(),
      );

      return true;
    },
    enabled:
      onPageThatCanDoFullApiHydration || isDexieEmpty || requiresFullHydrate,
  });

  return {
    isAPIHydrating,
    isDoingFullHydrate: isDexieEmpty,
    requiresFullHydrate,
    setRequiresFullHydrateToTrue,
  };
};

const useHydrate = () => {
  /* PERFORMANCE NOTE: For the best performance we load things in this order
   * 1. Sidebar
   * 2. Page
   * 3. App
   *
   * We hydrate the sidebar first to get a good initial loading UX
   *
   * For the current page, we generally do the local hydration first, and then the latest hydration
   * so users see the data as soon as possible, and then we do the latest hydration
   * to get eventual consistency with the DB
   *
   * lastly, we hydrate the app when possible so that page switching seems instant
   * A lot of the hydration work is completed in a web worker to avoid blocking the main thread
   *
   * With the hydration ledger, we can track what has been hydrated and what hasn't
   * so we can avoid hydrating the same data twice
   * This gives major flexibility and critical guarantees:
   * - We get the flexibility that any page can declare it's own hydration strategy
   * - We get the flexibility that any page begin without a hydration strategy
   *   OR it can opt out of ever having it's own hydration strategy
   * - We get the guarantee that the app will be hydrated for all of it's entities and data
   * - We get the guarantee that every entity and data will be hydrated exactly once
   * - We get the guarantee that if a page has a hydration strategy that has a mistake,
   *   it will be healed by the app hydration
   */

  useHydrateSidebarAndPage();
  const {
    isAPIHydrating,
    isDoingFullHydrate,
    requiresFullHydrate,
    setRequiresFullHydrateToTrue,
  } = useHydrateApp();

  return {
    isLoading: false,
    isAPIHydrating,
    isDoingFullHydrate,
    requiresFullHydrate,
    setRequiresFullHydrateToTrue,
  };
};
